/**
 * Bundle 173 - Manual payment override dialog (super-admin only).
 *
 * Unblocks users stuck in the payment loop by minting a verified
 * `payment_transactions` row + firing the canonical
 * `_finalize_registration_on_paid` side effects (ticket, confirmation
 * email, points, matchmaker eligibility). The amount honors any
 * committed Golden Ticket redemption via the Bundle 171 C4 helper on
 * the backend, so the recorded amount matches what the user was
 * asked to send.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';
import api from '@/utils/api';

const DEFAULT_REASON =
  'Manual confirmation by admin - payment received off-platform.';
const METHODS = [
  { value: 'zelle', label: 'Zelle' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

const ManualPaymentOverrideDialog = ({
  open, onClose, registration, event, defaultMethod, onSuccess,
}) => {
  const [method, setMethod] = useState(defaultMethod || 'zelle');
  const [reason, setReason] = useState(DEFAULT_REASON);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const displayName = (
    registration?.answers?.preferred_first_name
    || registration?.answers?.full_name
    || registration?.email
    || registration?.id
  );
  const currentStatus = (
    registration?.payment_status || registration?.status || 'unknown'
  );

  const submit = async () => {
    if (!registration?.id) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post(
        `/admin/registrations/${registration.id}/mark-paid-manual`,
        { method, reason: reason.trim() },
      );
      onSuccess?.(data);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(
        (detail && (detail.message || detail))
        || 'Manual override failed. Try again.',
      );
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="sm:max-w-[520px]"
        data-testid="manual-override-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary-700" />
            Mark this user as paid manually
          </DialogTitle>
          <DialogDescription>
            Super-admin only. Fires the same ticket + email + points
            side effects as an auto-verified payment.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 space-y-1">
            <div>
              <span className="text-neutral-500">Attendee:</span>{' '}
              <span
                className="font-medium"
                data-testid="manual-override-name"
              >{displayName}</span>
            </div>
            <div>
              <span className="text-neutral-500">Email:</span>{' '}
              <span data-testid="manual-override-email">
                {registration?.email || '-'}
              </span>
            </div>
            <div>
              <span className="text-neutral-500">Event:</span>{' '}
              <span>{event?.title || registration?.event_id}</span>
            </div>
            <div>
              <span className="text-neutral-500">Current status:</span>{' '}
              <span
                className="font-mono text-xs"
                data-testid="manual-override-current-status"
              >{currentStatus}</span>
            </div>
          </div>
          <div>
            <label
              htmlFor="mo-method"
              className="block text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1"
            >
              Payment method (records on the audit trail)
            </label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger
                id="mo-method"
                data-testid="manual-override-method"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METHODS.map((m) => (
                  <SelectItem
                    key={m.value} value={m.value}
                    data-testid={`manual-override-method-${m.value}`}
                  >
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label
              htmlFor="mo-reason"
              className="block text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1"
            >
              Reason (audit trail, min 5 chars)
            </label>
            <Textarea
              id="mo-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="manual-override-reason"
            />
          </div>
          {error ? (
            <div
              className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-2 text-red-900"
              role="alert"
              data-testid="manual-override-error"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="text-xs">{error}</div>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={busy}
            data-testid="manual-override-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || reason.trim().length < 5}
            data-testid="manual-override-confirm"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : null}
            Confirm mark paid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManualPaymentOverrideDialog;
